import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Plus,
  RotateCcw,
  Send,
  Users,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

// Sprint 09I — ONE generic event-communication center for every SVJ Tickets
// event, not a second StapKompas-only mailing module. StapKompas route/
// distance segmentation and route-info personalization are additive UI shown
// ONLY when `companionAppEnabled` — an ordinary event never sees any of it.
// Mirrors the exact admin patterns already established in
// AdminEventCompanion.jsx (authAxios, toast, svj-* design tokens, card
// wrappers) — no new admin UI system introduced.

const MAILING_TYPE_LABELS = {
  participant_info: "Deelnemersinformatie",
  practical_info: "Praktische informatie",
  final_reminder: "Laatste herinnering",
  important_change: "Belangrijke wijziging",
  thank_you: "Bedankmail",
  route_info: "Route-informatie",
  custom: "Vrije mail",
};

const STATUS_LABELS = {
  draft: "Concept",
  scheduled: "Gepland",
  sending: "Wordt verstuurd",
  sent: "Verstuurd",
  partial_failed: "Deels mislukt",
  failed: "Mislukt",
  cancelled: "Geannuleerd",
};

const STATUS_STYLES = {
  draft: "bg-slate-500/15 text-slate-300",
  scheduled: "bg-svj-cyan/15 text-svj-cyan",
  sending: "bg-amber-500/15 text-amber-400",
  sent: "bg-emerald-500/15 text-emerald-400",
  partial_failed: "bg-amber-500/15 text-amber-400",
  failed: "bg-red-500/15 text-red-400",
  cancelled: "bg-slate-500/15 text-slate-400",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${STATUS_STYLES[status] || "bg-slate-500/15 text-slate-300"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function toDateTimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Displays a UTC ISO timestamp as Europe/Amsterdam local time — the backend
// always stores/returns UTC; only display formatting needs to know the event
// timezone convention (see server.py's _parse_local_amsterdam_datetime).
function formatAmsterdam(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("nl-NL", {
      timeZone: "Europe/Amsterdam",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function AdminEventMailings({ eventId, authAxios, companionAppEnabled }) {
  const [mailings, setMailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMailingId, setActiveMailingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/mailings`);
      setMailings(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  if (!eventId) return null;

  const createDraft = async () => {
    setCreating(true);
    try {
      const { data } = await authAxios.post(`/admin/events/${eventId}/mailings`, {
        name: "Nieuwe mailing",
        mailing_type: "practical_info",
        subject: "",
        body_text: "",
        segment_type: "all_paid",
        segment_config: {},
        include_route_info: false,
      });
      await load();
      setActiveMailingId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  if (activeMailingId) {
    return (
      <MailingDetail
        eventId={eventId}
        mailingId={activeMailingId}
        authAxios={authAxios}
        companionAppEnabled={companionAppEnabled}
        onBack={() => {
          setActiveMailingId(null);
          load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Communicatie</div>
          <span className="text-xs text-slate-400">({mailings.length})</span>
        </div>
        <button
          type="button"
          onClick={createDraft}
          disabled={creating}
          data-testid="a4d-mailing-create"
          className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Nieuwe mailing
        </button>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Stuur aanvullende event-informatie naar je ticketkopers — praktische informatie, een laatste herinnering, een
        belangrijke wijziging, of (bij StapKompas-events) gepersonaliseerde route-informatie. De bestaande
        ticketbevestiging blijft hier volledig los van.
      </p>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}
      {!loading && mailings.length === 0 && <div className="text-sm text-slate-400">Nog geen mailings voor dit event.</div>}

      <div className="space-y-2">
        {mailings.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setActiveMailingId(m.id)}
            data-testid="a4d-mailing-row"
            className="w-full text-left flex items-center justify-between gap-3 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border hover:border-svj-cyan/50 transition-colors"
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{m.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                {MAILING_TYPE_LABELS[m.mailing_type] || m.mailing_type}
                {m.subject ? ` · ${m.subject}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {m.status === "sent" || m.status === "partial_failed" ? (
                <span className="text-xs text-slate-400">
                  {m.success_count}/{m.recipient_count} gelukt
                </span>
              ) : null}
              {m.scheduled_at && m.status === "scheduled" && (
                <span className="text-xs text-slate-400 hidden sm:inline">{formatAmsterdam(m.scheduled_at)}</span>
              )}
              <StatusBadge status={m.status} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Mailing detail: draft wizard, or status + delivery panel once sent/scheduled --

const STEPS = ["Type", "Doelgroep", "Inhoud", "Voorbeeld", "Testmail", "Verzenden"];

function MailingDetail({ eventId, mailingId, authAxios, companionAppEnabled, onBack }) {
  const [mailing, setMailing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/mailings/${mailingId}`);
      setMailing(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailingId]);

  if (loading || !mailing) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Laden…
      </div>
    );
  }

  const isDraft = mailing.status === "draft";

  return (
    <div>
      <button type="button" onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> Terug naar overzicht
      </button>

      {isDraft ? (
        <MailingWizard
          eventId={eventId}
          mailing={mailing}
          authAxios={authAxios}
          companionAppEnabled={companionAppEnabled}
          step={step}
          setStep={setStep}
          onSaved={load}
          onSent={() => {
            load();
            onBack();
          }}
        />
      ) : (
        <MailingStatusPanel eventId={eventId} mailing={mailing} authAxios={authAxios} onChanged={load} />
      )}
    </div>
  );
}

function MailingWizard({ eventId, mailing, authAxios, companionAppEnabled, step, setStep, onSaved, onSent }) {
  const [form, setForm] = useState({
    name: mailing.name || "",
    mailing_type: mailing.mailing_type || "practical_info",
    subject: mailing.subject || "",
    preheader: mailing.preheader || "",
    heading: mailing.heading || "",
    body_text: mailing.body_text || "",
    cta_label: mailing.cta_label || "",
    cta_url: mailing.cta_url || "",
    include_route_info: !!mailing.include_route_info,
    segment_type: mailing.segment_type || "all_paid",
    segment_config: mailing.segment_config || {},
  });
  const [saving, setSaving] = useState(false);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      await authAxios.patch(`/admin/mailings/${mailing.id}`, { ...form, ...extra });
      await onSaved();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Blocks advancing out of Doelgroep with an incomplete segment selection —
  // otherwise "ticket_type gekozen maar geen tickettype aangevinkt" silently
  // resolves to 0 ontvangers on the next step, indistinguishable from a real
  // backend problem. Mirrors the exact same rule the backend's own
  // preview-recipients endpoint enforces (segment_incomplete).
  const segmentIncomplete = () => {
    if (form.segment_type === "ticket_type") return !(form.segment_config.ticket_type_ids || []).length;
    if (form.segment_type === "route_group") return !(form.segment_config.route_group_ids || []).length;
    if (form.segment_type === "manual") return !(form.segment_config.order_ids || []).length;
    return false;
  };

  const goNext = async () => {
    if (step === 1 && segmentIncomplete()) {
      toast.error("Kies eerst minimaal één item voor deze doelgroep.");
      return;
    }
    const ok = await save();
    if (ok) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-5 overflow-x-auto" data-testid="a4d-mailing-steps">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-1.5 shrink-0">
            <span
              className={`h-6 min-w-6 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                i === step ? "bg-svj-cyan text-svj-bg" : i < step ? "bg-emerald-500/20 text-emerald-400" : "bg-svj-bg text-slate-500 border border-svj-border"
              }`}
            >
              {i + 1}
            </span>
            <span className={`text-xs font-semibold ${i === step ? "text-white" : "text-slate-500"}`}>{label}</span>
            {i < STEPS.length - 1 && <span className="text-slate-600 px-1">›</span>}
          </div>
        ))}
      </div>

      {step === 0 && <StepType form={form} setForm={setForm} companionAppEnabled={companionAppEnabled} />}
      {step === 1 && <StepAudience eventId={eventId} authAxios={authAxios} form={form} setForm={setForm} companionAppEnabled={companionAppEnabled} />}
      {step === 2 && <StepContent form={form} setForm={setForm} companionAppEnabled={companionAppEnabled} />}
      {step === 3 && <StepPreview mailingId={mailing.id} authAxios={authAxios} />}
      {step === 4 && <StepTest mailingId={mailing.id} authAxios={authAxios} />}
      {step === 5 && <StepSend eventId={eventId} mailing={mailing} authAxios={authAxios} onSent={onSent} />}

      {step < 5 && (
        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 0}
            className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-4 py-2 disabled:opacity-40"
          >
            Vorige
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={saving}
            data-testid="a4d-mailing-next"
            className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-5 py-2 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Volgende <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function FieldLabel({ children }) {
  return <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">{children}</span>;
}

const inputClass = "mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan";
const textareaClass = "mt-1 w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan";

function StepType({ form, setForm, companionAppEnabled }) {
  const types = Object.entries(MAILING_TYPE_LABELS).filter(([key]) => key !== "route_info" || companionAppEnabled);
  return (
    <div className="space-y-4">
      <label className="block">
        <FieldLabel>Interne naam</FieldLabel>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Bijv. Parkeerinformatie" />
      </label>
      <div>
        <FieldLabel>Type mailing</FieldLabel>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {types.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setForm({ ...form, mailing_type: key })}
              data-testid={`a4d-mailing-type-${key}`}
              className={`text-left px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                form.mailing_type === key ? "border-svj-cyan bg-svj-cyan/10 text-white" : "border-svj-border bg-svj-bg text-slate-300 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepAudience({ eventId, authAxios, form, setForm, companionAppEnabled }) {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [routeGroups, setRouteGroups] = useState([]);

  useEffect(() => {
    authAxios
      .get(`/admin/events/${eventId}/ticket-types`)
      .then(({ data }) => setTicketTypes(data.items || data || []))
      .catch(() => setTicketTypes([]));
    if (companionAppEnabled) {
      authAxios
        .get(`/admin/events/${eventId}/route-groups`)
        .then(({ data }) => setRouteGroups(data.items || []))
        .catch(() => setRouteGroups([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const segmentTypes = [
    { key: "all_paid", label: "Alle betaalde ontvangers" },
    { key: "ticket_type", label: "Specifiek tickettype" },
    ...(companionAppEnabled ? [{ key: "route_group", label: "Routekeuze / afstand" }] : []),
    { key: "manual", label: "Handmatige selectie" },
  ];

  const toggleIn = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Doelgroep</FieldLabel>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {segmentTypes.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setForm({ ...form, segment_type: s.key, segment_config: {} })}
              data-testid={`a4d-mailing-segment-${s.key}`}
              className={`text-left px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                form.segment_type === s.key ? "border-svj-cyan bg-svj-cyan/10 text-white" : "border-svj-border bg-svj-bg text-slate-300 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {form.segment_type === "ticket_type" && (
        <div>
          <FieldLabel>Tickettypes</FieldLabel>
          <div className="mt-2 space-y-1.5">
            {ticketTypes.length === 0 && <div className="text-xs text-slate-500">Geen tickettypes gevonden.</div>}
            {ticketTypes.map((tt) => {
              const selected = (form.segment_config.ticket_type_ids || []).includes(tt.id);
              return (
                <label key={tt.id} className="flex items-center gap-2 text-sm text-slate-200">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() =>
                      setForm({
                        ...form,
                        segment_config: { ticket_type_ids: toggleIn(form.segment_config.ticket_type_ids || [], tt.id) },
                      })
                    }
                  />
                  {tt.name}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {form.segment_type === "route_group" && (
        <div>
          <FieldLabel>Routekeuzes</FieldLabel>
          <div className="mt-2 space-y-1.5">
            {routeGroups.length === 0 && <div className="text-xs text-slate-500">Geen routekeuzes gevonden.</div>}
            {routeGroups.map((rg) => {
              const selected = (form.segment_config.route_group_ids || []).includes(rg.id);
              return (
                <label key={rg.id} className="flex items-center gap-2 text-sm text-slate-200">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() =>
                      setForm({
                        ...form,
                        segment_config: { route_group_ids: toggleIn(form.segment_config.route_group_ids || [], rg.id) },
                      })
                    }
                  />
                  {rg.name}
                  {rg.distance_km ? ` (${rg.distance_km} km)` : ""}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {form.segment_type === "manual" && (
        <label className="block">
          <FieldLabel>Order-ID&apos;s (één per regel)</FieldLabel>
          <textarea
            rows={4}
            value={(form.segment_config.order_ids || []).join("\n")}
            onChange={(e) =>
              setForm({
                ...form,
                segment_config: { order_ids: e.target.value.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean) },
              })
            }
            className={textareaClass}
            placeholder="order-uuid-1&#10;order-uuid-2"
          />
        </label>
      )}
    </div>
  );
}

function StepContent({ form, setForm, companionAppEnabled }) {
  return (
    <div className="space-y-4">
      <label className="block">
        <FieldLabel>E-mailonderwerp</FieldLabel>
        <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputClass} maxLength={200} />
      </label>
      <label className="block">
        <FieldLabel>Preheader (optioneel)</FieldLabel>
        <input value={form.preheader} onChange={(e) => setForm({ ...form, preheader: e.target.value })} className={inputClass} maxLength={200} />
      </label>
      <label className="block">
        <FieldLabel>Titel in de e-mail (optioneel)</FieldLabel>
        <input value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} className={inputClass} maxLength={200} />
      </label>
      <label className="block">
        <FieldLabel>Inhoud</FieldLabel>
        <textarea
          rows={8}
          value={form.body_text}
          onChange={(e) => setForm({ ...form, body_text: e.target.value })}
          className={textareaClass}
          placeholder={"Beste deelnemer,\n\nGebruik een lege regel voor een nieuwe alinea. **vet** wordt vetgedrukt weergegeven."}
        />
        <span className="mt-1 block text-[11px] text-slate-500">Lege regel = nieuwe alinea. **tekst** wordt vetgedrukt.</span>
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <FieldLabel>Knoptekst (optioneel)</FieldLabel>
          <input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className={inputClass} placeholder="Bijv. Bekijk parkeerinfo" />
        </label>
        <label className="block">
          <FieldLabel>Knoplink</FieldLabel>
          <input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className={inputClass} placeholder="https://…" />
        </label>
      </div>
      {companionAppEnabled && (
        <label className="flex items-center gap-2 text-sm text-slate-200">
          <input type="checkbox" checked={form.include_route_info} onChange={(e) => setForm({ ...form, include_route_info: e.target.checked })} />
          Route-informatie toevoegen (per ontvanger automatisch de juiste route + PDF)
        </label>
      )}
    </div>
  );
}

function StepPreview({ mailingId, authAxios }) {
  const [recipients, setRecipients] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    Promise.all([
      authAxios.post(`/admin/mailings/${mailingId}/preview-recipients`, {}),
      authAxios.post(`/admin/mailings/${mailingId}/preview-email`, {}),
    ])
      .then(([r, e]) => {
        if (cancelled) return;
        setRecipients(r.data);
        setEmailPreview(e.data);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || e.message))
      .finally(() => !cancelled && setLoadingPreview(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailingId]);

  if (loadingPreview) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Ontvangers en e-mail worden berekend…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {recipients && (
        <div className="rounded-lg bg-svj-bg/60 border border-svj-border p-4" data-testid="a4d-mailing-recipient-summary">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-svj-cyan" />
            <span className="text-sm font-bold text-white">{recipients.recipient_count} ontvangers</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-slate-400">
            <div>
              <div className="text-white font-semibold">{recipients.order_count}</div> orders
            </div>
            <div>
              <div className="text-white font-semibold">{recipients.ticket_count}</div> tickets
            </div>
            <div>
              <div className="text-white font-semibold">{recipients.deduplicated_count}</div> samengevoegd
            </div>
            <div>
              <div className="text-white font-semibold">{recipients.excluded_cancelled + recipients.excluded_unpaid}</div> uitgesloten
            </div>
          </div>
          {(recipients.excluded_cancelled > 0 || recipients.excluded_unpaid > 0) && (
            <div className="mt-2 text-[11px] text-slate-500">
              {recipients.excluded_cancelled} geannuleerd/terugbetaald, {recipients.excluded_unpaid} niet (volledig) betaald — deze ontvangen deze mailing niet.
            </div>
          )}
          {recipients.message && (
            <div
              className="mt-3 flex items-start gap-2 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"
              data-testid="a4d-mailing-recipient-warning"
            >
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{recipients.message}</span>
            </div>
          )}
        </div>
      )}
      {emailPreview && (
        <div>
          <FieldLabel>Voorbeeld e-mail</FieldLabel>
          <div className="mt-2 rounded-lg overflow-hidden border border-svj-border">
            <iframe title="E-mail voorbeeld" srcDoc={emailPreview.html} className="w-full bg-white" style={{ height: 480 }} />
          </div>
        </div>
      )}
    </div>
  );
}

function StepTest({ mailingId, authAxios }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!email.trim()) return toast.error("Vul een e-mailadres in");
    setSending(true);
    setSent(false);
    try {
      await authAxios.post(`/admin/mailings/${mailingId}/test-send`, { email: email.trim() });
      toast.success("Testmail verstuurd");
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">
        Verstuur een testmail voordat je deze mailing echt verstuurt. Een testmail telt niet mee als bezorging aan een
        echte ontvanger.
      </p>
      <div className="flex gap-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          placeholder="jij@voorbeeld.nl"
          className={`${inputClass} mt-0`}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending}
          data-testid="a4d-mailing-test-send"
          className="shrink-0 rounded-lg bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 disabled:opacity-60 inline-flex items-center gap-1.5"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Verstuur testmail
        </button>
      </div>
      {sent && (
        <div className="text-xs text-emerald-400 inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" /> Testmail verstuurd naar {email}
        </div>
      )}
    </div>
  );
}

function StepSend({ eventId, mailing, authAxios, onSent }) {
  const [recipientCount, setRecipientCount] = useState(null);
  const [recipientMessage, setRecipientMessage] = useState(null);
  const [mode, setMode] = useState("now");
  const [scheduledLocal, setScheduledLocal] = useState(toDateTimeLocalValue(new Date(Date.now() + 60 * 60 * 1000)));
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    authAxios
      .post(`/admin/mailings/${mailing.id}/preview-recipients`, {})
      .then(({ data }) => {
        setRecipientCount(data.recipient_count);
        setRecipientMessage(data.message || null);
      })
      .catch(() => setRecipientCount(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailing.id]);

  // Never allow a confirmed production send/schedule for 0 recipients — the
  // backend enforces this too (schedule/send-now both reject it), this is
  // purely so the admin sees a disabled button and a clear reason instead of
  // clicking through to a server error.
  const blocked = recipientCount === 0;

  const doSend = async () => {
    setSending(true);
    try {
      if (mode === "now") {
        await authAxios.post(`/admin/mailings/${mailing.id}/send-now`);
        toast.success("Mailing wordt verstuurd");
      } else {
        await authAxios.post(`/admin/mailings/${mailing.id}/schedule`, { scheduled_at_local: scheduledLocal });
        toast.success("Mailing ingepland");
      }
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode("now")}
          className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-semibold ${mode === "now" ? "border-svj-cyan bg-svj-cyan/10 text-white" : "border-svj-border bg-svj-bg text-slate-300"}`}
        >
          <Send className="h-4 w-4 inline mr-1.5 -mt-0.5" /> Nu verzenden
        </button>
        <button
          type="button"
          onClick={() => setMode("schedule")}
          className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-semibold ${mode === "schedule" ? "border-svj-cyan bg-svj-cyan/10 text-white" : "border-svj-border bg-svj-bg text-slate-300"}`}
        >
          <Calendar className="h-4 w-4 inline mr-1.5 -mt-0.5" /> Inplannen
        </button>
      </div>

      {mode === "schedule" && (
        <label className="block">
          <FieldLabel>Datum en tijd (Nederlandse tijd)</FieldLabel>
          <input type="datetime-local" value={scheduledLocal} onChange={(e) => setScheduledLocal(e.target.value)} className={inputClass} />
        </label>
      )}

      {blocked && (
        <div
          className="flex items-start gap-2 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"
          data-testid="a4d-mailing-send-blocked"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{recipientMessage || "Geen geldige ontvangers gevonden voor deze doelgroep — er kan niets worden verstuurd."}</span>
        </div>
      )}

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={blocked || recipientCount === null}
          data-testid="a4d-mailing-send-open-confirm"
          className="w-full rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {mode === "now" ? <Send className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
          {mode === "now" ? "Nu verzenden" : "Mailing inplannen"}
        </button>
      ) : (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 space-y-3" data-testid="a4d-mailing-send-confirm">
          <div className="flex items-start gap-2 text-sm text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Je staat op het punt deze e-mail naar {recipientCount === null ? "…" : recipientCount} ontvangers te{" "}
              {mode === "now" ? "sturen." : `plannen voor ${scheduledLocal.replace("T", " ")}.`}
            </span>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setConfirming(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">
              Annuleren
            </button>
            <button
              type="button"
              onClick={doSend}
              disabled={sending}
              data-testid="a4d-mailing-send-confirmed"
              className="rounded-lg bg-amber-500 text-svj-bg font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Ja, versturen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MailingStatusPanel({ mailing, authAxios, onChanged }) {
  const [deliveries, setDeliveries] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadDeliveries = async () => {
    try {
      const { data } = await authAxios.get(`/admin/mailings/${mailing.id}/deliveries`, {
        params: showAll ? {} : { status: "failed" },
      });
      setDeliveries(data.items || []);
    } catch {
      // best-effort — the counts above already convey the important summary
    }
  };

  useEffect(() => {
    loadDeliveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailing.id, showAll]);

  const counts = mailing.delivery_counts || {};
  const failedCount = counts.failed || 0;

  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await authAxios.post(`/admin/mailings/${mailing.id}/retry-failed`);
      toast.success(`${data.now_sent} van ${data.retried} alsnog gelukt`);
      await onChanged();
      await loadDeliveries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRetrying(false);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await authAxios.post(`/admin/mailings/${mailing.id}/cancel`);
      toast.success("Mailing geannuleerd");
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-bold text-white">{mailing.name}</div>
          <div className="text-sm text-slate-400">{mailing.subject}</div>
        </div>
        <StatusBadge status={mailing.status} />
      </div>

      {mailing.scheduled_at && (mailing.status === "scheduled") && (
        <div className="text-sm text-slate-300 inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-svj-cyan" /> Gepland voor {formatAmsterdam(mailing.scheduled_at)}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-svj-bg/60 border border-svj-border p-3 text-center">
          <div className="text-xl font-bold text-white">{mailing.recipient_count}</div>
          <div className="text-[11px] text-slate-400 uppercase tracking-wide">Ontvangers</div>
        </div>
        <div className="rounded-lg bg-svj-bg/60 border border-svj-border p-3 text-center">
          <div className="text-xl font-bold text-emerald-400">{mailing.success_count}</div>
          <div className="text-[11px] text-slate-400 uppercase tracking-wide">Gelukt</div>
        </div>
        <div className="rounded-lg bg-svj-bg/60 border border-svj-border p-3 text-center">
          <div className="text-xl font-bold text-red-400">{mailing.failed_count}</div>
          <div className="text-[11px] text-slate-400 uppercase tracking-wide">Mislukt</div>
        </div>
      </div>

      <div className="flex gap-2">
        {failedCount > 0 && (
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            data-testid="a4d-mailing-retry-failed"
            className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} {failedCount} mislukte opnieuw proberen
          </button>
        )}
        {mailing.status === "scheduled" && (
          <button
            type="button"
            onClick={cancel}
            disabled={cancelling}
            className="rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-semibold px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />} Annuleren
          </button>
        )}
      </div>

      {(mailing.status === "sent" || mailing.status === "partial_failed" || mailing.status === "failed") && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <FieldLabel>{showAll ? "Alle bezorgingen" : "Mislukte bezorgingen"}</FieldLabel>
            <button type="button" onClick={() => setShowAll((v) => !v)} className="text-xs font-semibold text-svj-cyan">
              {showAll ? "Toon alleen mislukt" : "Toon alle"}
            </button>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {deliveries.length === 0 && <div className="text-xs text-slate-500">Geen bezorgingen om te tonen.</div>}
            {deliveries.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border">
                <span className="text-slate-300 truncate">{d.contact_name || d.contact_email}</span>
                <span className={`font-semibold ${d.status === "sent" ? "text-emerald-400" : d.status === "failed" ? "text-red-400" : "text-slate-400"}`}>
                  {d.status === "sent" ? "Verstuurd" : d.status === "failed" ? "Mislukt" : d.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
